import React, { useEffect, useState } from 'react';
import { FiDollarSign, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { ImSpinner } from 'react-icons/im';
import Modal from '../common/Modal';
import ButtonLoading from '../ButtonLoading';
import SendOTP from '../SendOTP';
import BankSelect from './BankSelect';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency } from '../../utils/currencyUtils';

const FundWithdrawModal = ({ isOpen, onClose, school, schoolId, fundId, availableBalance, onSuccess }) => {
  const [banks, setBanks] = useState([]);
  const [amount, setAmount] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [narration, setNarration] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);

  const amountNum = parseFloat(amount) || 0;
  const availableNum = parseFloat(availableBalance) || 0;
  const hasSufficientBalance = amountNum > 0 && availableNum >= amountNum;
  const showBreakdown = amountNum > 0;

  useEffect(() => {
    if (!isOpen) return;
    const fetchBanks = async () => {
      try {
        const { data } = await userRequest.get('/banking/withdrawal-banks');
        const result = data?.bank_codes || [];
        const seen = new Set();
        setBanks(result.filter(b => {
          if (seen.has(b.bank_code)) return false;
          seen.add(b.bank_code);
          return true;
        }));
      } catch (err) {
        console.error(err);
      }
    };
    fetchBanks();
  }, [isOpen]);

  const resolveAccount = async (code, number) => {
    if (!code || !number) return;
    setResolving(true);
    setAccountName('');
    setError('');
    try {
      const { data } = await userRequest.post('banking/validate-account', {
        accounts: [{ bankCode: code, accountNumber: number }]
      });
      const result = data?.result?.[0];
      setAccountName(result?.beneficiaryName || '');
      if (!result?.beneficiaryName) setError('Could not resolve account name.');
    } catch {
      setError('Error, unable to resolve account!');
    } finally {
      setResolving(false);
    }
  };

  const reset = () => {
    setAmount('');
    setBankCode('');
    setAccountName('');
    setAccountNumber('');
    setNarration('');
    setOtp('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!amount || !bankCode || !accountNumber || !accountName || !otp) {
      setError('Please fill all the fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await userRequest.post(`/schools/${schoolId}/funds/${fundId}/withdrawals`, {
        amount: Number(amount),
        destinationBankCode: bankCode,
        destinationAccountNumber: accountNumber,
        destinationAccountName: accountName,
        narration: narration || 'Fund Withdrawal',
        otp,
      });
      setSuccess(true);
      onSuccess?.();
      setTimeout(() => { reset(); onClose(); }, 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Withdrawal failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Withdraw from Fund" icon={<FiDollarSign className="text-indigo-600 text-lg" />}>
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm mb-4">
          <FiCheckCircle className="mt-0.5 shrink-0" />
          <span><b>Success!</b> Withdrawal processed.</span>
        </div>
      )}

      <form onSubmit={submitHandler} className="space-y-4">
        <SendOTP school={school} />

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Amount (₦)</label>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="e.g. 5000 (min. 100)"
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
          {showBreakdown && (
            <div className={`flex justify-between text-xs pt-2 ${hasSufficientBalance ? 'text-green-600' : 'text-red-500'}`}>
              <span>Available balance</span>
              <span>{formatCurrency(availableNum)}</span>
            </div>
          )}
          {showBreakdown && !hasSufficientBalance && (
            <div className="flex items-center gap-1.5 text-xs text-red-500 pt-1">
              <FiAlertCircle className="shrink-0" />
              Insufficient balance for this withdrawal
            </div>
          )}
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Account Number</label>
          <input
            type="text"
            value={accountNumber}
            onChange={e => setAccountNumber(e.target.value)}
            onBlur={() => resolveAccount(bankCode, accountNumber)}
            placeholder="Enter 10-digit account number"
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Bank Name</label>
          <BankSelect
            banks={banks}
            value={bankCode}
            onChange={(bank) => { setBankCode(bank.bank_code); resolveAccount(bank.bank_code, accountNumber); }}
            disabled={banks.length === 0}
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">
            Account Name
            {resolving && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-indigo-500 font-normal">
                <ImSpinner className="animate-spin" /> Verifying...
              </span>
            )}
          </label>
          <input
            type="text"
            value={accountName}
            readOnly
            placeholder="Auto-filled after verification"
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm text-gray-500 cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Narration</label>
          <input
            type="text"
            value={narration}
            onChange={e => setNarration(e.target.value)}
            placeholder="e.g. Fund withdrawal"
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">OTP</label>
          <input
            type="text"
            value={otp}
            onChange={e => setOtp(e.target.value)}
            placeholder="Enter generated OTP"
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        {loading ? (
          <ButtonLoading styles="w-full text-white bg-indigo-500 font-medium rounded-full text-sm px-5 py-2.5 text-center" />
        ) : (
          <button
            type="submit"
            disabled={resolving || !accountName || (showBreakdown && !hasSufficientBalance)}
            className="w-full text-white bg-indigo-600 hover:bg-indigo-700 font-medium rounded-full text-sm px-5 py-2.5 text-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Withdrawal
          </button>
        )}
      </form>
    </Modal>
  );
};

export default FundWithdrawModal;
